import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function toYMD(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  return "";
}

function toDMY(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  const onlyDate = s.slice(0, 10);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return "";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

function getMachineValue(m) {
  if (!m) return "";
  if (typeof m === "string") return m;

  return (
    m.machine_name ||
    m.machineName ||
    m.machine_no ||
    m.machineNo ||
    m.mc_no ||
    m.mcNo ||
    m.name ||
    m.machine ||
    m.code ||
    m.value ||
    ""
  );
}

export default function Crush2Edit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [date, setDate] = useState("");
  const [receivedBy, setReceivedBy] = useState("");

  const [machines, setMachines] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      sourceCrush1LineId: "",
      machineNo: "",
      operatorName: "",
      timeHour: "",
      itemCode: "",
      itemName: "",
      remainingKg: 0,
      resultKg: "",
      receiveDate: "",
    },
  ]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [mRes, empRes, hRes, lRes, sRes] = await Promise.all([
          api.get(`/api/machines?type=Extruder`),
          api.get("/api/employees"),
          api.get(`/api/crush/crush2/headers`),
          api.get(`/api/crush/crush2/headers/${id}/lines`),
          api.get(`/api/crush/crush2/sources/crush1`),
        ]);

        const mArr = toArray(mRes.data);
        const empArr = toArray(empRes.data);
        const headersArr = toArray(hRes.data);
        const linesArr = toArray(lRes.data);
        const srcArr = toArray(sRes.data);

        if (!alive) return;

        setMachines(mArr);
        setEmployees(empArr);
        setSources(srcArr);

        const header = headersArr.find((x) => String(x.headerId) === String(id));

        setDate(toDMY(header?.productionDate) || toDMY(todayYMD()));
        setReceivedBy(header?.receivedBy || "");

        const mapped = linesArr.map((l) => {
          const srcId = l.sourceCrush1LineId ?? l.source_crush1_line_id ?? "";
          const src = srcArr.find(
            (s) => String(s.crush1LineId) === String(srcId)
          );

          const lineKg = num(l.resultKg ?? l.result_kg);
          const baseRemaining = num(src?.remainingKg);
          const correctedRemaining = src ? baseRemaining + lineKg : baseRemaining;

          return {
            sourceCrush1LineId: srcId ? String(srcId) : "",
            machineNo: l.machineNo ?? l.machine_no ?? src?.machineNo ?? "",
            operatorName: l.operatorName ?? l.operator_name ?? "",
            timeHour: l.timeHour ?? l.time_hour ?? "",
            itemCode: l.itemCode ?? l.item_code ?? src?.itemCode ?? "",
            itemName: l.itemName ?? l.item_name ?? src?.itemName ?? "",
            remainingKg: correctedRemaining,
            resultKg: String(lineKg || ""),
            receiveDate: toDMY(l.receiveDate ?? l.receive_date) || "",
          };
        });

        setRows(
          mapped.length
            ? mapped
            : [
                {
                  sourceCrush1LineId: "",
                  machineNo: "",
                  operatorName: "",
                  timeHour: "",
                  itemCode: "",
                  itemName: "",
                  remainingKg: 0,
                  resultKg: "",
                  receiveDate: "",
                },
              ]
        );
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to load Crush 2 entry"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const sourcesById = useMemo(() => {
    const map = new Map();
    sources.forEach((s) => map.set(String(s.crush1LineId), s));
    return map;
  }, [sources]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const machineOptions = useMemo(() => {
    return machines
      .map((m) => {
        const value = getMachineValue(m);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  }, [machines]);

  const sourceOptions = useMemo(() => {
    return sources.map((s) => ({
      value: String(s.crush1LineId),
      label: `#${s.crush1LineId} | ${s.itemCode || "-"} | Rem: ${num(
        s.remainingKg
      ).toFixed(3)} KG`,
      searchText: `${s.crush1LineId} ${s.itemCode || ""} ${s.itemName || ""} ${
        s.machineNo || ""
      } ${num(s.remainingKg).toFixed(3)}`.toLowerCase(),
    }));
  }, [sources]);

  const itemCodeOptions = useMemo(() => {
    return Array.from(
      new Set(sources.map((s) => s.itemCode).filter(Boolean))
    ).map((code) => ({
      value: code,
      label: code,
      searchText: String(code).toLowerCase(),
    }));
  }, [sources]);

  const grandTotalKg = useMemo(
    () => rows.reduce((s, r) => s + num(r.resultKg), 0),
    [rows]
  );

  const totalRows = rows.length;

  function updateRow(i, patch) {
    setRows((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }

  function addRow() {
    setRows((p) => [
      ...p,
      {
        sourceCrush1LineId: "",
        machineNo: "",
        operatorName: "",
        timeHour: "",
        itemCode: "",
        itemName: "",
        remainingKg: 0,
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((p) => (p.length === 1 ? p : p.filter((_, x) => x !== i)));
  }

  function onPickSource(rowIndex, sourceId) {
    const src = sourcesById.get(String(sourceId));

    if (!src) {
      updateRow(rowIndex, {
        sourceCrush1LineId: "",
        machineNo: "",
        itemCode: "",
        itemName: "",
        remainingKg: 0,
        resultKg: "",
      });
      return;
    }

    updateRow(rowIndex, {
      sourceCrush1LineId: String(src.crush1LineId),
      machineNo: src.machineNo || "",
      itemCode: src.itemCode || "",
      itemName: src.itemName || "",
      remainingKg: num(src.remainingKg),
    });
  }

  async function saveUpdate() {
    const productionDate = toYMD(date);

    if (!productionDate) return alert("Date required");
    if (!receivedBy.trim()) return alert("Received By required");

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];

      if (!r.sourceCrush1LineId) {
        return alert(`Row ${i + 1}: Select Source (Crush1)`);
      }

      if (!r.machineNo) {
        return alert(`Row ${i + 1}: Select Machine`);
      }

      if (!r.operatorName) {
        return alert(`Row ${i + 1}: Select Operator Name`);
      }

      if (!r.itemCode) {
        return alert(`Row ${i + 1}: Select Item Code`);
      }

      const rk = num(r.resultKg);
      if (rk <= 0) {
        return alert(`Row ${i + 1}: Result KG must be > 0`);
      }

      const rem = num(r.remainingKg);
      if (rk > rem + 0.0000001) {
        return alert(
          `Row ${i + 1}: Result KG cannot be more than Remaining KG`
        );
      }
    }

    const payloadLines = rows.map((r) => ({
      sourceCrush1LineId: Number(r.sourceCrush1LineId),
      machineNo: r.machineNo,
      operatorName: r.operatorName,
      timeHour: r.timeHour,
      itemCode: r.itemCode,
      resultKg: r.resultKg,
      receiveDate: toYMD(r.receiveDate),
    }));

    try {
      await api.put(`/api/crush/crush2/headers/${id}`, {
        productionDate,
        receivedBy,
        lines: payloadLines,
      });

      alert("Updated successfully");
      navigate("/crush-2");
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Update failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  const filterSelectOption = (option, inputValue) => {
    const text =
      `${option.label} ${option.data.searchText || ""}`.toLowerCase();
    return text.includes(String(inputValue).toLowerCase());
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Edit Crush 2 Entry (ID: {id})</h2>

        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={saveUpdate}>
            Save Changes
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/crush-2")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={toYMD(date)}
              onChange={(e) => setDate(toDMY(e.target.value))}
            />
          </div>

          <div className="col-md-5">
            <label className="form-label">Received By</label>
            <Select
              options={employeeOptions}
              value={
                employeeOptions.find((opt) => opt.value === receivedBy) || null
              }
              onChange={(selected) =>
                setReceivedBy(selected ? selected.value : "")
              }
              placeholder="-- Select Employee --"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No employee found"}
              filterOption={filterSelectOption}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>SN</th>
                <th style={{ width: 320 }}>Source (Crush1)</th>
                <th style={{ width: 180 }}>Machine</th>
                <th style={{ width: 220 }}>Operator Name</th>
                <th style={{ width: 160 }}>Time / Hour</th>
                <th style={{ width: 160 }}>Item Code</th>
                <th>Item Name</th>
                <th style={{ width: 160 }}>Remaining KG</th>
                <th style={{ width: 140 }}>Result KG</th>
                <th style={{ width: 170 }}>Receive Date</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td className="align-middle">
                    <Select
                      options={sourceOptions}
                      value={
                        sourceOptions.find(
                          (opt) => opt.value === String(r.sourceCrush1LineId)
                        ) || null
                      }
                      onChange={(selected) =>
                        onPickSource(i, selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No source found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={machineOptions}
                      value={
                        machineOptions.find((opt) => opt.value === r.machineNo) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(i, {
                          machineNo: selected ? selected.value : "",
                        })
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No machine found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === r.operatorName
                        ) || null
                      }
                      onChange={(selected) =>
                        updateRow(i, {
                          operatorName: selected ? selected.value : "",
                        })
                      }
                      placeholder="-- Select Employee --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No employee found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) => updateRow(i, { timeHour: e.target.value })}
                      placeholder="e.g. 11:30 / 8"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={itemCodeOptions}
                      value={
                        itemCodeOptions.find((opt) => opt.value === r.itemCode) ||
                        null
                      }
                      onChange={(selected) => {
                        const code = selected ? selected.value : "";
                        const found = sources.find((s) => s.itemCode === code);
                        updateRow(i, {
                          itemCode: code,
                          itemName: found?.itemName || r.itemName || "",
                        });
                      }}
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No item found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm text-end"
                      value={num(r.remainingKg).toFixed(3)}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.resultKg}
                      onChange={(e) => updateRow(i, { resultKg: e.target.value })}
                      placeholder="e.g. 200"
                    />
                  </td>

                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toYMD(r.receiveDate)}
                      onChange={(e) =>
                        updateRow(i, { receiveDate: toDMY(e.target.value) })
                      }
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b>Total Rows: {totalRows}</b>
          <b>Grand Total KG: {grandTotalKg.toFixed(2)}</b>
        </div>
      </div>
    </div>
  );
}